import React, { useMemo } from 'react';
// @ts-ignore
import styles from './../PatientProfileV2.module.scss';
import { UserEditedMeta } from './PatientProfileInformation';
import FirstUpper from '../../FirstUpper';
import FormattedDate from '../../FormattedDate';
import useTranslation from '../../../Hooks/useTranslation';
import usePatientMeta from '../Hooks/usePatientMeta';
import { useAppContext } from '../../../Services/AppContext';
import { UserInvitation } from '../../../graphql/graphql';
import Text from '../../Base/Text/Text';
import PatientProfileInvitations from './PatientProfileInvitations';
import PatientProfileEditButton from '../../../Modules/EArchive/Components/PatientProfileEdit/PatientProfileEditButton';
import FormattedMomentDate, {
	DateFormat,
} from '../../../Modules/Base/Components/FormattedMomentDate/FormattedMomentDate';
import PatientProfilePart from './PatientProfilePart';
import FlyerPrintButtonAfterRegistration from '../../../Modules/EArchive/Components/FlyerPrint/FlyerPrintButton/FlyerPrintButtonAfterRegistration';
import usePatientIdentification from '../../../Modules/Patient/Hooks/usePatientIdentification';

interface Props {
	user: UserEditedMeta;
	showCzechServices: boolean;
	onProfileEdit: () => void;
}

interface Parent {
	id?: string;
	fullName?: string;
	phoneNumber?: string;
	email?: string;
	invitations: UserInvitation[];
}

const PatientProfileInformationChildren: React.FC<Props> = ({
	user,
	showCzechServices,
	onProfileEdit,
}) => {
	const { t } = useTranslation();
	const insuranceCompany = user.profile?.insuranceCompanyTermItem;
	const { identification } = usePatientIdentification(user);

	const { metaIsLoading, patientDiagnosis } = usePatientMeta(
		user.id,
		user.originalMeta,
	);
	const { isLatam } = useAppContext();

	const parents: Parent[] = useMemo(() => {
		const tempParents: Parent[] = [];

		if (!user.parents || user.parents?.length < 1) {
			const legalRepresentative = user.meta.legalRepresentative;
			if (legalRepresentative) {
				tempParents.push({
					id: user.id,
					fullName:
						legalRepresentative.firstName + ' ' + legalRepresentative.lastName,
					phoneNumber:
						legalRepresentative.contactPhoneNumber ||
						legalRepresentative.phoneNumber,
					email: legalRepresentative.contactEmail || legalRepresentative.email,
					invitations: [
						{
							code: legalRepresentative.invitationCode,
							createdAt: legalRepresentative.invitationCodeCreatedAt,
							used: false,
						},
					],
				});
			}
		}

		user.parents?.forEach(parent => {
			tempParents.push({
				id: parent?.id,
				fullName: parent?.profile?.fullName ?? undefined,
				phoneNumber: parent?.formattedPhoneNumber ?? undefined,
				email: parent?.profile?.contactEmail ?? parent?.email ?? undefined,
				invitations: parent?.invitations?.length
					? (parent?.invitations as UserInvitation[])
					: [],
			});
		});

		return tempParents;
	}, [user]);

	return (
		<>
			{user?.profile?.birthDate && (
				<div className={styles.item}>
					<Text
						className={styles.name}
						tx='base.shared.dateOfBirth'
						firstUpper
						colon
					/>
					<div className={styles.text}>
						<FormattedMomentDate
							date={user.profile.birthDate}
							format={DateFormat.DATE}
						/>{' '}
						{!isLatam && identification.value && (
							<>
								<span>{t(identification.title)}</span> {identification.value}
							</>
						)}
					</div>
				</div>
			)}
			{user.profile?.address?.fullAddress && (
				<div className={styles.item}>
					<Text
						className={styles.name}
						tx='base.shared.address'
						firstUpper
						colon
					/>
					<div className={styles.text}>
						{showCzechServices && user.csszResult && (
							<span>
								{user.csszResult.result ? (
									<img
										src='/dist/icons/v2/badge-check.svg'
										alt='test'
										className={styles.icon}
									/>
								) : null}
								<FirstUpper>
									{user.csszResult.result
										? t('base.verified')
										: t('component.csszResult.addressNotVerified')}
								</FirstUpper>{' '}
								<FormattedDate date={user.csszResult.checkedAt} />
							</span>
						)}{' '}
						{user.profile?.address?.fullAddress}
					</div>
				</div>
			)}
			{(insuranceCompany || user.vzpResult) && (
				<div className={styles.item}>
					<Text
						className={styles.name}
						tx='base.insuranceShort'
						firstUpper
						colon
					/>
					<div className={styles.text}>
						{' '}
						{showCzechServices && user.vzpResult && (
							<span>
								{user.vzpResult.result ? (
									<img
										src='/dist/icons/v2/badge-check.svg'
										alt='test'
										className={styles.icon}
									/>
								) : null}
								<FirstUpper>
									{user.vzpResult.result
										? t('base.verified')
										: t('component.vzpResult.insuranceNotVerified')}
								</FirstUpper>{' '}
								<FormattedDate date={user.vzpResult.checkedAt} />
							</span>
						)}{' '}
						{insuranceCompany?.value2 + ' - ' + insuranceCompany?.value}
					</div>
				</div>
			)}
			{user.vzpResult?.result?.prubehPojisteni?.druhPojisteni && (
				<div className={styles.item}>
					<Text
						className={styles.name}
						tx='base.typeOfInsurance'
						firstUpper
						colon
					/>
					<div className={styles.text}>
						{user.vzpResult?.result?.prubehPojisteni?.druhPojisteni}
					</div>
				</div>
			)}
			{user?.meta?.insurance && user?.meta?.insurance.hasInsurance && (
				<>
					<div className={styles.item}>
						<Text
							className={styles.name}
							tx='base.shared.insuranceCompany'
							firstUpper
							colon
						/>
						<div className={styles.text}>{user.meta.insurance.companyName}</div>
					</div>
					<div className={styles.item}>
						<Text
							className={styles.name}
							tx='base.typeOfInsurance'
							firstUpper
							colon
						/>
						<div className={styles.text}>{user.meta.insurance.type}</div>
					</div>
				</>
			)}
			{user.profile?.formattedGender && (
				<div className={styles.item}>
					<Text
						className={styles.name}
						tx='base.shared.gender'
						firstUpper
						colon
					/>
					<div className={styles.text}>{user.profile.formattedGender}</div>
				</div>
			)}
			{user.profile?.nationalityTermItem && (
				<div className={styles.item}>
					<Text
						className={styles.name}
						tx='base.citizenship'
						firstUpper
						colon
					/>
					<div className={styles.text}>
						{user.profile.nationalityTermItem.name}
					</div>
				</div>
			)}
			{!metaIsLoading && patientDiagnosis?.mainDiagnosis && (
				<div className={styles.item}>
					<Text
						className={styles.name}
						tx='base.mainDiagnosis'
						firstUpper
						colon
					/>
					<div className={styles.text}>
						{patientDiagnosis?.mainDiagnosis?.label}
					</div>
				</div>
			)}
			<div className={styles.item}>
				<div className={styles.name}>
					<FirstUpper>{t('base.registrationDate')}</FirstUpper>:
				</div>
				<div className={styles.text}>
					<FormattedMomentDate
						date={user.createdAt}
						format={DateFormat.DATE_TIME}
					/>
				</div>
			</div>
			{user.meta.birthPlace && (
				<PatientProfilePart
					label='component.patientManualRegistrationHungaryForm.birthPlace'
					value={user.meta.birthPlace}
				/>
			)}
			{user.meta.mothersFullname && (
				<PatientProfilePart
					label='component.patientManualRegistrationHungaryForm.mothersFullname'
					value={user.meta.mothersFullname}
				/>
			)}
			{user.note && (
				<PatientProfilePart label='base.shared.note' value={user.note} />
			)}
			{parents.length === 0 && user.meta?.childInvitationCode?.code && (
				<div className={styles.item}>
					<Text tx='base.code' className={styles.name} firstUpper colon />
					<div className={styles.code}>
						{user.meta.childInvitationCode.code}
					</div>
					<FlyerPrintButtonAfterRegistration
						patientId={user.id}
						buttonTitle='base.print'
					/>
				</div>
			)}
			{parents && parents.length > 0 && (
				<div className={styles.legalRepresentative}>
					{parents.map(parent => (
						<>
							{parent.fullName && (
								<div className={styles.item}>
									<Text
										className={styles.legalRepresentativeTitle}
										tx='base.shared.legalRepresentative'
										firstUpper
										colon
									/>
									<div className={styles.text}>{parent.fullName}</div>
									{parent.id !== user.id && (
										<div className={styles.editButtonWrapper}>
											<PatientProfileEditButton
												onSuccess={onProfileEdit}
												userId={parent.id}
												childId={user.id}
											/>
										</div>
									)}
								</div>
							)}
							{parent.email && (
								<div className={styles.item}>
									<Text
										className={styles.name}
										tx='base.shared.email'
										firstUpper
										colon
									/>
									<div className={styles.text}>{parent.email}</div>
								</div>
							)}
							{parent.phoneNumber && (
								<div className={styles.item}>
									<Text
										className={styles.name}
										tx='base.shared.phone'
										firstUpper
										colon
									/>
									<div className={styles.text}>{parent.phoneNumber}</div>
								</div>
							)}
							<PatientProfileInvitations
								invitations={parent.invitations}
								childId={user.id}
							/>
						</>
					))}
				</div>
			)}
		</>
	);
};

export default PatientProfileInformationChildren;
